<script setup lang="ts">
import { stats } from '~/data/stats';
import type { TOverviewMode } from './index.vue';

const CountUp = defineAsyncComponent(() => import('vue-countup-v3'));

const props = defineProps<{ mode: TOverviewMode }>();
const parent = useTemplateRef('parent');
const isVisible = ref(false);

const data = computed(() => stats[props.mode]);

useIntersectionObserver(
    parent,
    ([entry]) => {
        if (entry?.isIntersecting) {
            isVisible.value = true;
            stop();
        }
    },
);
</script>

<template>
    <div ref="parent">
        <DevicesMobile>
            <div class="stats mt-3 mb-10">
                <span class="h1">
                    {{ data[data.length - 1]?.number }}
                    <span v-if="data[data.length - 1]?.symbol" class="text-secondary-600">
                        {{ data[data.length - 1]?.symbol }}
                    </span>
                </span>
                <p class="text-body1 text-gray-400">{{ data[data.length - 1]?.title }}</p>
            </div>
            <div class="w-full grid! grid-cols-2 gap-y-10 overflow-hidden">
                <div
                    v-for="item in data.slice(0, data.length - 1)"
                    :key="item.title"
                    class="stats"
                >
                    <span class="h1">
                        <span v-if="item.isPercent">%</span>
                        {{ item.number.toLocaleString() }}
                        <span v-if="item.symbol" class="text-secondary-600">{{ item.symbol }}</span>
                    </span>
                    <p class="text-body1 text-gray-400">{{ item.title }}</p>
                </div>
            </div>
        </DevicesMobile>

        <DevicesDesktop>
            <div v-if="isVisible" class="w-full flex items-center mb-25 mt-16 overflow-hidden">
                <div 
                    v-for="item in data"
                    :key="item.title"
                    class="stats"
                >
                    <span class="h1">
                        <span v-if="item.isPercent">%</span>
                        <ClientOnly>
                            <CountUp
                                :end-val="item.number"
                                :decimalPlaces="Number(item?.isDecimal)"
                                :duration="3"
                                class="inline"
                            />
                        </ClientOnly>
                        <span v-if="item.symbol" class="text-secondary-600">{{ item.symbol }}</span>
                    </span>
                    <p class="text-body1 text-gray-400">{{ item.title }}</p>
                </div>
            </div>
            <div v-else class="w-full h-17.5 my-25" />
        </DevicesDesktop>
    </div>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.stats {
    @apply
    flex flex-col items-center lg:w-45
}
</style>